import { createSlug } from './urlHelpers';
import { getCommissionAdjustedPrice } from './commission';
import { buildVehicleSchema } from './vehicleSchema';

/**
 * Generates schema.org structured data for a listing page: a Product + Vehicle
 * built by the shared vehicleSchema builder (the destinations page describes
 * the same van with it), at full detail.
 *
 * @param {Object} listing - Full listing object from Sharetribe SDK
 * @param {Array} reviews - Array of review objects
 * @param {Object} monthlyTimeSlots - Availability data by month
 * @param {Object} config - Marketplace configuration (from useConfiguration hook)
 * @param {number} [providerCommission] - platform default provider commission percentage, for computing the customer-facing offer price
 * @returns {Object|null} Schema.org structured data object, or null if listing invalid
 *
 * @example
 * const schema = generateListingSchema(currentListing, reviews, monthlyTimeSlots, config, providerCommission);
 * <Page schema={schema} ... />
 */
export const generateListingSchema = (listing, reviews, monthlyTimeSlots, config, providerCommission = 0) => {
  if (!listing?.id) return null;

  const { title, description, price, publicData } = listing.attributes;
  const listingId = listing.id.uuid;
  // Crawlers/searchers are never the listing's own author, so the schema
  // advertises the customer-facing price — the same call the page's visible
  // title price makes for non-owners (ListingPageCarousel/CoverPhoto titlePrice).
  const offerPrice = getCommissionAdjustedPrice(listing, providerCommission) || price;

  // The slug comes from the display title — this must match the canonical page
  // URL, which Sharetribe derives from attributes.title (not the SEO title).
  const slug = createSlug(title || '');
  const listingUrl = `${config.marketplaceRootURL}/l/${slug}/${listingId}`;

  // Image with fallback priority (high quality → social → medium)
  const imageUrl =
    listing.images?.[0]?.attributes?.variants?.['listing-card-6x']?.url ||
    listing.images?.[0]?.attributes?.variants?.facebook?.url ||
    listing.images?.[0]?.attributes?.variants?.['scaled-medium']?.url;

  // Available when any month has a timeslot
  const hasAvailability =
    monthlyTimeSlots &&
    Object.values(monthlyTimeSlots).some(month => month.timeSlots?.length > 0);

  // Rating from the reviews actually loaded on the page
  const validReviews = reviews?.filter(r => r.attributes?.rating && r.attributes.rating > 0) || [];
  const rating =
    validReviews.length > 0
      ? {
          value: Number(
            (validReviews.reduce((sum, r) => sum + r.attributes.rating, 0) / validReviews.length).toFixed(1)
          ),
          count: validReviews.length,
        }
      : null;

  return {
    '@context': 'https://schema.org',
    '@type': ['Product', 'Vehicle'],
    ...buildVehicleSchema({
      listingId,
      // Mirrors the visible product name (the H1 / display title), consistent
      // with the url slug above.
      name: title,
      description,
      url: listingUrl,
      imageUrl,
      publicData,
      price: offerPrice,
      rating,
      availability: hasAvailability ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      marketplaceName: config.marketplaceName,
      listingFields: config.listing?.listingFields,
      categories: config.categoryConfiguration?.categories,
      detail: 'full',
    }),
  };
};
